<script>
	const utilities = require('utilities');

	module.exports = {
		components: {
			'validated-textarea': require('components/validated-textarea.vue').default
		},
		props: {
			entry: {
				default: null
			},
			article: {
				default: null
			},
			editable: {
				default: false
			},
			inlineEdit: {
				default: false
			},
			assigned: {
				default: true,
				type: Boolean
			},
			student: {
				default: null
			},
			onArticleClick: {
				default: null,
				type: Function
			},
			onStudentClick: {
				default: null,
				type: Function
			},
			onEdit: {
				default: null,
				type: Function
			}
		},
		data() {
			return {
				editContent: null,
				editRevision: false
			};
		},
		computed: {
			showEditIcon() {
				let editable = this.editable;
				let editing = this.editing;
				let entry = this.entry;
				let submitted = entry.s === 's';
				let pastDue = entry.pastDue;

				return editable && !editing && (submitted || pastDue);
			},
			editing() {
				return this.editContent !== null;
			},
			initialPlaceholder() {
				let word = this.inlineEdit ? 'Edit ' : 'Add to ';
				return word + 'your Book of Knowledge Entry';
			},
			studentName() {
				const entry = this.entry;
				return entry && entry.name;
			},
			isDecodable() {
				const article = this.article;
				return article && article.decodableTeacherEdition;
			},
			initialSubmission() {
				let entry = this.entry;
				let studentName = this.studentName;
				let content = 'You did not write anything in your Book of Knowledge.';

				if (entry.b) {
					content = entry.b;
				} else if (studentName) {
					content = studentName + ' did not write anything in the Book of Knowledge.';
				}

				return content;
			},
			logText() {
				const articleType = this.isDecodable ? 'decodable' : 'article';
				const article = this.article;
				const articleTitle = article && article.title;
				const dateString = utilities.getShortDateString(this.entry.tf);

				const student = this.student;
				const studentNameFallback = student && student.name; // teacher BBOK
				const studentName = this.studentName || studentNameFallback;

				if (!articleTitle) {
					// Assumption: All submitted entries without article data are decodables
					if (studentName) {
						return `A decodable was read on ${dateString}`;
					}

					return `You read a decodable on ${dateString}`;
				}

				if (studentName) {
					return `The ${articleType} "${articleTitle}" was read on ${dateString}`;
				}

				return `You read the ${articleType} "${articleTitle}" on ${dateString}`;
			}
		},
		methods: {
			onEditStartInternal(editRevision) {
				let entry = this.entry;

				if (this.editable) {
					if (editRevision) {
						this.editContent = entry.brev || '';
						this.editRevision = editRevision;
					} else {
						this.editContent = entry.b || '';
					}
				}
			},
			onEditEndInternal() {
				let entry = this.entry;
				if (this.editable && this.onEdit) {
					this.onEdit(entry, this.editContent, true, this.editRevision);
				}
				this.editContent = null;
				this.editRevision = false;
			},
			onEditInternal() {
				let entry = this.entry;
				if (this.editable && this.onEdit) {
					this.onEdit(entry, this.editContent, null, this.editRevision);
				}
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';

	.bok-entry {
		position: relative;
		padding: 1rem 0.5rem 1rem 1rem;
		background-color: $c-blue-lighter;
		color: $c-slate-dark;
		border-radius: 4px;
		vertical-align: top;

		.student-name {
			font-weight: 500;
			margin-bottom: 0.5rem;

			&.student-link {
				cursor: pointer;

				span {
					color: $c-blue;
				}

				&:hover span {
					color: $c-slate-dark;
				}
			}
		}

		.bok-submission {
			display: flex;
			flex-direction: column;

			&.revision {
				margin-top: $size-medium;
			}

			.article-title-container {
				.link {
					cursor: pointer;
					text-decoration: underline;

					&:hover {
						color: $c-slate-dark;
					}
				}
			}

			.empty {
				font-style: italic;
			}

			.article-title-container,
			.date {
				font-size: 0.75rem;
			}

			&.initial > .article-title-container,
			&.revision > .date,
			&.no-article > .date {
				margin-top: 0.25rem;
			}

			.bok-edit-form {
				text-align: center;

				.validated-textarea {
					textarea {
						min-height: 8rem;
					}

					.placeholder {
						text-align: left;
					}
				}
			}
		}

		.bok-revise-icon {
			cursor: pointer;
			&:hover {
				color: $c-blue;
			}
		}

		.bok-actions-left {
			position: absolute;
			top: 0.5rem;
			left: -1rem;
		}

		.bok-actions {
			float: right;
			padding-left: 0.7em;
		}

		.bok-actions,
		.bok-actions-left {
			.entry-promoted {
				padding: 0.3rem;
				background-color: $c-gold;
				border-radius: 1rem;

				svg g {
					stroke: $c-blue;
				}
			}
			.add-to-cbok {
				cursor: pointer;
				display: inline;

				svg g {
					stroke: $c-slate-dark;
				}

				&.in-cbok {
					svg g {
						stroke: $c-blue;
					}
				}
			}
		}
	}

	@media print {
		.bok-entry {
			page-break-inside: avoid;

			.article-title-container,
			.date {
				font-weight: 500;
			}

			.bok-actions {
				display: none;
			}
		}
	}
</style>
<template>
	<div
		v-if="assigned && !isDecodable && entry && (entry.b || article)"
		class="bok-entry"
		:data-aid="entry.aid"
		:data-uid="entry.suids"
		:data-pid="entry.pid">
		<div class="bok-actions">
			<slot name="actions"></slot>
		</div>
		<div class="bok-actions-left">
			<slot name="actions-left"></slot>
		</div>
		<div
			v-if="studentName"
			class="student-name-container">
			<a
				v-if="onStudentClick"
				class="student-name student-link"
				@click="onStudentClick(entry.uid)">
				{{ studentName }}
			</a>
			<span
				v-else
				class="student-name"
				>{{ studentName }}</span
			>
			said:
		</div>
		<div
			class="bok-submission initial"
			:class="{
				'no-article': !article
			}">
			<div
				v-show="!editing || editRevision || !inlineEdit"
				class="bok-submission-content"
				:class="{
					empty: !entry.b
				}">
				{{ initialSubmission }}
				<button
					v-if="(!entry.brev || inlineEdit) && showEditIcon"
					class="button -link bok-revise"
					@click="onEditStartInternal(!inlineEdit)"
					aria-label="Edit">
					<i class="fa-solid fa-pen-to-square bok-revise-icon"></i>
				</button>
			</div>
			<div
				v-if="editing && (!entry.brev || !editRevision)"
				class="bok-edit-form">
				<form @submit.prevent="onEditEndInternal">
					<validated-textarea
						class="print-hidden"
						@change.native="onEditInternal"
						:value="editContent"
						@input.native="editContent = $event.target.value"
						:placeholder="initialPlaceholder">
					</validated-textarea>
					<button
						class="button"
						type="submit">
						Submit
					</button>
				</form>
			</div>
			<div
				v-if="article"
				class="article-title-container">
				from
				<a
					v-if="onArticleClick"
					class="article-title link"
					href="javascript:void(0)"
					@click="onArticleClick(article)"
					>{{ article.title }}</a
				>
				<span
					v-else
					class="article-title"
					>{{ article.title }}</span
				>
			</div>
			<div
				v-if="entry.s === 's' && entry.bets"
				class="date">
				Edited <span>{{ entry.bets | dateShort }}</span>
			</div>
			<div
				v-if="entry.s === 's' && !entry.bets && entry.tf"
				class="date">
				Submitted <span>{{ entry.tf | dateShort }}</span>
			</div>
			<div
				v-if="entry.s !== 's' && !entry.pastDue"
				class="date">
				In Progress
			</div>
			<div
				v-if="entry.s !== 's' && entry.pastDue"
				class="date">
				Incomplete
			</div>
		</div>
		<div
			v-if="entry.brev"
			class="bok-submission revision">
			<div
				class="bok-submission-content"
				v-show="!editing || !editRevision">
				<div>
					{{ entry.brev }}
					<button
						v-if="entry.brev && showEditIcon"
						class="button -link bok-revise"
						aria-label="edit"
						@click="onEditStartInternal(true)">
						<i class="fa-solid fa-pen-to-square bok-revise-icon"></i>
					</button>
				</div>
			</div>
			<div
				v-if="editing && editRevision"
				class="bok-edit-form">
				<form @submit.prevent="onEditEndInternal">
					<validated-textarea
						class="print-hidden"
						@change.native="onEditInternal"
						:value="editContent"
						@input.native="editContent = $event.target.value"
						placeholder="Add to your Book of Knowledge Entry">
					</validated-textarea>
					<button
						class="button"
						type="submit">
						Submit
					</button>
				</form>
			</div>
			<div class="date">
				Edited <span>{{ entry.brevts | dateShort }}</span>
			</div>
		</div>
	</div>
	<div
		v-else-if="entry && entry.tf"
		class="bok-entry log-view text-italic"
		v-text="logText"></div>
</template>
